import React from 'react';
import classNames from 'classnames';

import styles from './SavedPaymentMethods.module.scss';
import type { ISavedPaymentMethod } from '@/common/interfaces/payment.interface';
import {
  SAVED_PAYMENT_METHODS_TITLE,
  SAVED_PM_ICONS,
  SAVED_PM_OR_DIVIDER_LABEL,
  SAVED_PM_REMOVE_HELP,
} from '@/constants/Order';
import { formatSavedPaymentMethodLabel } from '@/utilities/PaymentUtils';

interface ISavedPaymentMethodsProps {
  methods: ISavedPaymentMethod[];
  selectedToken: string | null;
  isLoading: boolean;
  onSelect: (token: string) => void;
}

const getPaymentMethodIcon = (paymentMethod: ISavedPaymentMethod): string => {
  if (paymentMethod.type === 'paypal') return SAVED_PM_ICONS.PAYPAL;
  if (paymentMethod.type === 'venmo') return SAVED_PM_ICONS.VENMO;
  if (paymentMethod.type === 'google_pay') return SAVED_PM_ICONS.GOOGLE_PAY;

  const cardType = paymentMethod.cardType?.toLowerCase() ?? '';
  if (cardType.includes('visa')) return SAVED_PM_ICONS.VISA;
  if (cardType.includes('master')) return SAVED_PM_ICONS.MASTERCARD;
  if (cardType.includes('american') || cardType.includes('amex')) return SAVED_PM_ICONS.AMEX;
  return SAVED_PM_ICONS.CARD;
};

const getExpiryLabel = (paymentMethod: ISavedPaymentMethod): string | null => {
  if (!paymentMethod.expirationMonth || !paymentMethod.expirationYear) return null;
  return `Expires ${paymentMethod.expirationMonth}/${paymentMethod.expirationYear}`;
};

const RadioIndicator = ({ selected }: { selected: boolean }) => (
  <span className={styles.radio} aria-hidden>
    {selected ? (
      <span className={styles.radioSelected}>
        <img
          src="/src/assets/svg/check-simple.svg"
          alt=""
          width={10}
          height={8}
          draggable={false}
        />
      </span>
    ) : (
      <span className={styles.radioUnselected} />
    )}
  </span>
);

/**
 * Checkout-only picker for the logged-in user's display-safe vaulted methods.
 * It mirrors the existing payment radio rows and keeps Braintree's own vault list hidden.
 */
const SavedPaymentMethods = ({
  methods,
  selectedToken,
  isLoading,
  onSelect,
}: ISavedPaymentMethodsProps) => {
  if (isLoading && methods.length === 0) {
    return (
      <section className={styles.section} aria-label={SAVED_PAYMENT_METHODS_TITLE}>
        <h3 className={styles.title}>{SAVED_PAYMENT_METHODS_TITLE}</h3>
        <div className={styles.skeleton} aria-label="Loading saved payment methods" />
      </section>
    );
  }

  if (methods.length === 0) return null;

  return (
    <section className={styles.section} aria-labelledby="saved-payment-methods-heading">
      <h3 id="saved-payment-methods-heading" className={styles.title}>
        {SAVED_PAYMENT_METHODS_TITLE}
      </h3>
      <div className={styles.methods} role="radiogroup" aria-label={SAVED_PAYMENT_METHODS_TITLE}>
        {methods.map((paymentMethod) => {
          const selected = selectedToken === paymentMethod.token;
          const expiryLabel = getExpiryLabel(paymentMethod);
          return (
            <label
              key={paymentMethod.token}
              className={classNames(styles.row, selected && styles.rowSelected)}
            >
              <input
                type="radio"
                name="savedPaymentMethod"
                value={paymentMethod.token}
                checked={selected}
                onChange={() => onSelect(paymentMethod.token)}
                className={styles.srOnly}
              />
              <RadioIndicator selected={selected} />
              <img src={getPaymentMethodIcon(paymentMethod)} className={styles.methodIcon} alt="" />
              <span
                className={classNames(styles.methodName, selected && styles.methodNameSelected)}
              >
                {formatSavedPaymentMethodLabel(paymentMethod)}
                {expiryLabel ? <span className={styles.methodMeta}>{expiryLabel}</span> : null}
              </span>
            </label>
          );
        })}
      </div>
      <p className={styles.help}>{SAVED_PM_REMOVE_HELP}</p>
      <div className={styles.orRow} aria-hidden>
        <span className={styles.orLine} />
        <span className={styles.orLabel}>{SAVED_PM_OR_DIVIDER_LABEL}</span>
        <span className={styles.orLine} />
      </div>
    </section>
  );
};

export default React.memo(SavedPaymentMethods);
